import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import * as reportsService from '../../services/api/reports';
import { useAuth } from '../../hooks/use-auth';
import { strings } from '../../localization/strings';
import { SimpleObject } from '../../entities/simple-object';
import { fillFromParams } from '../../services/crud/filter';
import UsageReportFilter from './components/usage-report-filter';
import FilterUsageReport from '../../entities/filter/usage-report';
import { UsageReport } from '../../entities/usage-report';
import AlertPrimary from '../../components/common/alerts/alert-primary';
import CommonAnimatedLoader from '../../components/common/animated/loader';

function ReportsUsagePageComponent() {
  const [queryParams, setQueryParams] = useSearchParams();

  const startDate = new Date();
  const endDate = new Date();

  startDate.setMonth(startDate.getMonth() - 1);
  startDate.setDate(1);
  endDate.setDate(0);

  const startFromLink = queryParams.get('starts_at') !== null ? new Date(queryParams.get('starts_at') as string) : null;
  const endFromLink = queryParams.get('ends_at') !== null ? new Date(queryParams.get('ends_at') as string) : null;

  const initialState: FilterUsageReport = {
    starts_at: startFromLink || startDate,
    ends_at: endFromLink || endDate,
  };

  const { token } = useAuth();
  const [params, setParams] = useState<FilterUsageReport>(
    fillFromParams<FilterUsageReport>(initialState, queryParams, { isDate: ['starts_at', 'ends_at'] }),
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [report, setReport] = useState<UsageReport>();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const result = await reportsService.getUsageReport(token, params);

        if (result) {
          setReport(result);
        }
        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
        setError((e as Error).message);
      }
    };

    fetchData();
  }, [token, params]);

  const setFilterData = (filterData: FilterUsageReport) => {
    const q: SimpleObject = {};

    for (const [key, value] of Object.entries(filterData)) {
      if (typeof value !== 'undefined' && value !== null) {
        q[key] = value;
      }
    }

    setParams({ ...filterData });
    setQueryParams({ ...q } as SimpleObject);
  };

  const exportData = async () => {
    const blob = await reportsService.exportUsageReport(token, params);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = downloadUrl;
    link.download = 'usage-report.xlsx';
    link.click();
    window.URL.revokeObjectURL(downloadUrl);
  };

  return (
    <>
      <header className="mb-3">
        <div className="flex space-x-3 justify-between">
          <h1 className="text-3xl font-bold leading-tight text-gray-900">{strings.navigation.reports.usage}</h1>
        </div>
      </header>
      <main className="w-full">
        <div className="sm:rounded-lg border border-gray-200 overflow-hidden">
          <UsageReportFilter onUpdate={setFilterData} filterState={params} onExport={exportData} />
        </div>

        {error && (
          <div className="mx-auto my-5">
            <AlertPrimary type="danger" title={error} />
          </div>
        )}

        {isLoading && <CommonAnimatedLoader />}

        {!isLoading && !!report && report.rows.length > 0 ? (
          <div className="mt-8 flow-root border-top-1 border-top-gray-200">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {strings.usageReport.client}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {strings.usageReport.photosEltaDownloads}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {strings.usageReport.photosEpaDownloads}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {strings.usageReport.videosViews}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {strings.usageReport.videosDownloads}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {strings.usageReport.total}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {report.rows.map((row) => (
                      <tr key={`usage-${row.company_id}`}>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{row.company_name}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                          {row.photos_elta_downloads}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                          {row.photos_epa_downloads}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{row.videos_views}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{row.videos_downloads}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm font-semibold text-gray-900">{row.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          !error &&
          !isLoading && (
            <div className="my-5">
              <AlertPrimary type="failed" text={strings.search.noResults} />
            </div>
          )
        )}
      </main>
    </>
  );
}

export default ReportsUsagePageComponent;
